// Ravencross visions — pixel-art panoramas that appear on the road in
// the Travel section. Shared between TravelPanel (roll + render as
// background) and CodexPanel (Vision Codex tab). State is persisted
// on the character server-side via PUT /api/character/travel-vision so
// the selected image survives refresh + logout/login.
import { api } from "@/lib/api";

export const TRAVEL_VISIONS = [
  {
    id: "bridge",
    url: "https://customer-assets-39nsmqrw.emergentagent.net/job_63119e2a-b574-4db4-ac80-4785975abbed/artifacts/abdr30h1_Ponte%20Ravencross.png",
    caption: "Il Ponte di Ravencross.",
  },
  {
    id: "road",
    url: "https://customer-assets-39nsmqrw.emergentagent.net/job_63119e2a-b574-4db4-ac80-4785975abbed/artifacts/uwoxmuh6_Strada%20per%20ravencross.png",
    caption: "La strada che porta a Ravencross.",
  },
  {
    id: "market",
    url: "https://customer-assets-39nsmqrw.emergentagent.net/job_63119e2a-b574-4db4-ac80-4785975abbed/artifacts/mqbi366o_strada%20commercio%20ravencross.png",
    caption: "La via del commercio nella città alta.",
  },
  {
    id: "vista",
    url: "https://customer-assets-39nsmqrw.emergentagent.net/job_63119e2a-b574-4db4-ac80-4785975abbed/artifacts/5qo1dtf2_Vista%20ravencross.png",
    caption: "Vista di Ravencross al tramonto.",
  },
];

// Anti-repeat + trigger tuning. The pick blocks the last N visions so
// panoramas don't cluster together.
export const VISION_MIN_STEPS  = 10;
export const VISION_CHANCE     = 0.35;
export const VISION_UNLOCKED_KEY  = "travelVisionUnlocked";  // still local (cosmetic Codex)
export const VISION_FIRST_SEEN_KEY = "travelVisionFirstSeen"; // {id: ISO ts}

// Draw the next vision from the pool, excluding the last two ids so
// panoramas don't cluster together. Falls back to the full pool if
// every candidate is blocked (pool < 3).
export function pickVision(history /*, lifetimeSteps unused */) {
  const blocked = new Set((history || []).slice(-2));
  const pool = TRAVEL_VISIONS.filter(v => !blocked.has(v.id));
  const draw = pool.length ? pool : TRAVEL_VISIONS;
  return draw[Math.floor(Math.random() * draw.length)];
}

export function loadUnlockedVisions() {
  try {
    const raw = JSON.parse(localStorage.getItem(VISION_UNLOCKED_KEY) || "[]");
    return new Set(Array.isArray(raw) ? raw : []);
  } catch { return new Set(); }
}

export function markVisionUnlocked(id) {
  const set = loadUnlockedVisions();
  const alreadyKnown = set.has(id);
  set.add(id);
  localStorage.setItem(VISION_UNLOCKED_KEY, JSON.stringify(Array.from(set)));
  // Timeline: only stamp the FIRST time this vision is seen so the
  // Codex badge shows an accurate discovery date.
  if (!alreadyKnown) {
    const seen = loadVisionFirstSeen();
    if (!seen[id]) {
      seen[id] = new Date().toISOString();
      localStorage.setItem(VISION_FIRST_SEEN_KEY, JSON.stringify(seen));
    }
  }
  return set;
}

// Load the {visionId: ISO timestamp} map that powers the Vision Codex
// timeline badges. Broken/empty state falls back to `{}` so callers
// don't need null checks.
export function loadVisionFirstSeen() {
  try {
    const raw = JSON.parse(localStorage.getItem(VISION_FIRST_SEEN_KEY) || "{}");
    return typeof raw === "object" && raw ? raw : {};
  } catch { return {}; }
}

// Restore vision object from an id (or null → null). Used to hydrate
// the persisted `travel_vision_state.current` into a full TRAVEL_VISIONS
// entry that TravelPanel can render.
export function findVisionById(id) {
  if (!id) return null;
  return TRAVEL_VISIONS.find(v => v.id === id) || null;
}

// Save the vision state on the server so it survives refresh + logout.
// Fire-and-forget — errors are swallowed because failing to persist a
// panorama roll must never block the walk itself.
export async function persistVisionState({ current, history, step_counter }) {
  try {
    await api.updateTravelVision({
      current: current || null,
      history: Array.isArray(history) ? history : [],
      step_counter: Number.isFinite(step_counter) ? step_counter : 0,
    });
  } catch { /* silent — cosmetic feature */ }
}
